/**
 * Runtime Content Database C# Template Renderer (Engine-Independent)
 * Creates a dictionary-based structure with template classes
 * 
 * Uses Grimoire.Data.Runtime abstractions for logging
 * Works with Unity, Godot, or standalone .NET
 */

import { ExportTemplate, NamingConvention, TemplateField } from '@/types/exportTemplates';
import type { StringsItem } from '@/lib/stores/stringsStore';
import { applyNamingConvention } from '../../core/naming';
import { extractFields } from '../../fieldMapping/extraction';
import { LookupContext } from '../../fieldMapping/references';
import { stripHtmlTags } from '@/lib/utils/documentExportUtils';

/**
 * Render Runtime Content Database C# template
 * Creates a dictionary-based structure with template classes
 * 
 * @param items - Items to export
 * @param template - Export template configuration
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data for reference resolution
 * @param tags - Optional tags data for reference resolution
 * @param allItemsForLookup - Optional all items for glossaryItem lookups
 * @param namingConventionOverride - Optional override for all naming conventions
 * @param customNamespace - Optional custom namespace (default: 'Grimoire.Data')
 * @param customClassName - Optional custom class name (default: 'Database')
 * @param stringsItems - Optional translation strings data
 * @param dialogs - Optional dialogs array for Dialogs section
 * @param documents - Optional documents array for document field resolution
 */
export function renderRuntimeDatabaseCSharpTemplate(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customNamespace?: string,
  customClassName?: string,
  stringsItems?: StringsItem[],
  dialogs?: any[],
  documents?: any[],
  gameLanguages?: string[]
): string {
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items,
    dialogs: dialogs,
    documents: documents
  };
  
  // When only one language is configured, suppress translation keys and export as plain strings
  const useTranslationKeys = (gameLanguages?.length || 1) > 1;
  
  // Create a map of string_id to StringsItem for quick lookup
  // When single-language mode, keep stringsMap empty so translation lookups fall through to plain strings
  const stringsMap = new Map<string, StringsItem>();
  if (stringsItems && useTranslationKeys) {
    stringsItems.forEach(stringItem => {
      stringsMap.set(stringItem.id, stringItem);
    });
  }

  // Use custom namespace and class name or defaults
  const namespace = customNamespace || 'Grimoire.Data';
  const className = customClassName || 'Database';
  
  // Determine naming convention: use override if provided, otherwise use template default
  const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;
  const templateConvention = namingConventionOverride || template.namingConventions.templateName;
  
  // Group items by blueprint to create template classes
  const blueprintGroups = new Map<string, { blueprint: any, items: any[] }>();
  
  items.forEach(item => {
    const blueprintId = item.blueprint_id;
    if (!blueprintId) return;
    
    if (!blueprintGroups.has(blueprintId)) {
      const blueprint = blueprints.find(bp => bp.id === blueprintId);
      if (blueprint) {
        blueprintGroups.set(blueprintId, { blueprint, items: [] });
      }
    }
    
    const group = blueprintGroups.get(blueprintId);
    if (group) {
      group.items.push(item);
    }
  });
  
  // Helper to get the template class name for a glossary item
  const getTemplateClassName = (item: any): string => {
    if (!item || !item.blueprint_id) return 'Template';
    
    // Find the blueprint for this item
    const blueprint = blueprints.find(bp => bp.id === item.blueprint_id);
    if (!blueprint) return 'Template';
    
    // Get the blueprint name and apply naming convention
    const blueprintName = blueprint.name || 'Unknown';
    return applyNamingConvention(blueprintName, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
  };
  
  // Helper to get C# type from mapped type string AND actual value
  const getCSharpType = (mappedType: string, actualValue?: any, isTranslatable: boolean = false, field?: TemplateField): string => {
    // Check if this is a reference field
    if (field?.isReference && field?.referenceType) {
      if (field.referenceType === 'types') {
        // For types, use VariableElement (references to Variables class)
        // Use field.isMultiple to determine array vs single, not actualValue
        return field.isMultiple ? 'VariableElement[]' : 'VariableElement';
      }
      if (field.referenceType === 'tags') {
        // For tags, use string (the value from the tag element)
        // Use field.isMultiple to determine array vs single, not actualValue
        return field.isMultiple ? 'string[]' : 'string';
      }
      if (field.referenceType === 'glossaryItem') {
        // Determine the specific blueprint type for strong typing
        let specificType = 'Template'; // fallback
        if (field.referenceData) {
          if (Array.isArray(field.referenceData)) {
            // For arrays, check if all items share the same blueprint_id
            const blueprintIds = field.referenceData
              .filter(item => item && item.blueprint_id)
              .map(item => item.blueprint_id);
            
            if (blueprintIds.length > 0) {
              const uniqueBlueprintIds = [...new Set(blueprintIds)];
              if (uniqueBlueprintIds.length === 1) {
                // All items share the same blueprint type
                const sharedBlueprintId = uniqueBlueprintIds[0];
                const refBlueprint = blueprints.find(bp => bp.id === sharedBlueprintId);
                if (refBlueprint) {
                  specificType = applyNamingConvention(refBlueprint.name || sharedBlueprintId, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
                }
              }
            }
          } else {
            // Single item reference
            const refItem = field.referenceData;
            if (refItem && refItem.blueprint_id) {
              const refBlueprint = blueprints.find(bp => bp.id === refItem.blueprint_id);
              if (refBlueprint) {
                specificType = applyNamingConvention(refBlueprint.name || refItem.blueprint_id, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
              }
            }
          }
        }
        return Array.isArray(actualValue) ? `${specificType}[]` : specificType;
      }
      if (field.referenceType === 'dialog') {
        // Dialog references use DialogSection as the base type
        // Use field.isMultiple to determine array vs single
        return field.isMultiple ? 'DialogSection[]' : 'DialogSection';
      }
      if (field.referenceType === 'document') {
        // Document fields are exported as arrays of DocumentBlock objects
        return 'DocumentBlock[]';
      }
    }
    
    // If translatable, use TranslationKey instead of string
    if (isTranslatable) {
      if (Array.isArray(actualValue)) return 'TranslationKey[]';
      return 'TranslationKey';
    }
    
    if (Array.isArray(actualValue)) return 'string[]';
    if (mappedType.includes('bool')) return 'bool';
    if (mappedType.includes('int')) {
      // If the actual value has decimals, use double instead of int
      if (typeof actualValue === 'number' && !Number.isInteger(actualValue)) return 'double';
      return 'int';
    }
    if (mappedType.includes('float')) return 'float';
    if (mappedType.includes('double')) return 'double';
    if (mappedType.includes('[]') || mappedType.includes('Array')) return 'string[]';
    return 'string';
  };
  
  // Helper to format value for C#
  const formatValueCSharp = (value: any, mappedType: string, field?: TemplateField): string => {
    // Handle reference fields - generate object references
    if (field?.isReference && field?.referenceData && field?.referenceType) {
      console.log(`🎯 formatValueCSharp: Reference field detected - type=${field.referenceType}`);
      
      if (field.referenceType === 'types') {
        // For types, generate Variables class references for type-safe access
        // Use field.isMultiple to determine output format, not just Array.isArray
        if (field.isMultiple) {
          // Multiple field - always output as array
          const dataArray = Array.isArray(field.referenceData) ? field.referenceData : (field.referenceData ? [field.referenceData] : []);
          const references = dataArray
            .map(refObj => {
              const parentTypeName = refObj._parentTypeName || 'UnknownType';
              const elementName = refObj.name || refObj.id || 'Unknown';
              const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
              const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
              return `Variables.${safeTypeName}.${safeElementName}`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of Variable references: new VariableElement[] { ${references} }`);
          return `new VariableElement[] { ${references} }`;
        } else {
          // Single VariableElement reference
          const refData = Array.isArray(field.referenceData) ? field.referenceData[0] : field.referenceData;
          if (!refData) {
            return 'null';
          }
          const parentTypeName = refData._parentTypeName || 'UnknownType';
          const elementName = refData.name || refData.id || 'Unknown';
          const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
          const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
          const variableReference = `Variables.${safeTypeName}.${safeElementName}`;
          console.log(`  ✅ Generated Variable reference: ${variableReference}`);
          return variableReference;
        }
      } else if (field.referenceType === 'tags') {
        // Use field.isMultiple to determine output format, not just Array.isArray
        if (field.isMultiple) {
          // Multiple field - always output as array
          const dataArray = Array.isArray(field.referenceData) ? field.referenceData : (field.referenceData ? [field.referenceData] : []);
          const values = dataArray
            .map(refObj => {
              const val = refObj.value || refObj.code || refObj.name || refObj.id;
              return `"${String(val).replace(/"/g, '\\"')}"`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of tag values: new[] { ${values} }`);
          return `new[] { ${values} }`;
        } else {
          // Single tag value
          const refData = Array.isArray(field.referenceData) ? field.referenceData[0] : field.referenceData;
          if (!refData) {
            return '""';
          }
          const val = refData.value || refData.code || refData.name || refData.id;
          const stringValue = `"${String(val).replace(/"/g, '\\"')}"`;
          console.log(`  ✅ Generated tag value: ${stringValue}`);
          return stringValue;
        }
      } else if (field.referenceType === 'glossaryItem') {
        if (Array.isArray(field.referenceData)) {
          const refs = field.referenceData.map(refObj => {
            const refClassName = getTemplateClassName(refObj);
            const itemKey = applyNamingConvention(refObj.name || refObj.id, namingConventionOverride || template.namingConventions.instanceName);
            const refFolderPath = folderPathResolver(refObj.folder);
            const pathKey = refFolderPath ? `${refFolderPath.replace(/^\//, '').replace(/\\/g, '/')}/${itemKey}` : itemKey;
            return `(${refClassName})Library["${pathKey}"]`;
          }).join(', ');
          console.log(`  ✅ Generated glossaryItem array reference: new[] { ${refs} }`);
          return `new[] { ${refs} }`;
        } else {
          const refClassName = getTemplateClassName(field.referenceData);
          const itemKey = applyNamingConvention(field.referenceData.name || field.referenceData.id, namingConventionOverride || template.namingConventions.instanceName);
          const refFolderPath = folderPathResolver(field.referenceData.folder);
          const pathKey = refFolderPath ? `${refFolderPath.replace(/^\//, '').replace(/\\/g, '/')}/${itemKey}` : itemKey;
          const ref = `(${refClassName})Library["${pathKey}"]`;
          console.log(`  ✅ Generated glossaryItem reference: ${ref}`);
          return ref;
        }
      } else if (field.referenceType === 'document') {
        // Document references - extract blocks from document data
        const document = field.referenceData;
        if (!document || !document.data) {
          console.log(`  ⚠️ Document has no data, returning empty array`);
          return 'new DocumentBlock[] { }';
        }
        
        // Document data is stored as DocumentData[] (array of sections)
        const blocks: Array<{ type: string; content: string; index: number }> = [];
        let currentIndex = 0;
        
        const documentData = Array.isArray(document.data) ? document.data : [document.data];
        
        documentData.forEach((section: any) => {
          if (section.blocks && Array.isArray(section.blocks)) {
            section.blocks.forEach((block: any) => {
              if (block.type === 'text' && block.content && block.content.trim()) {
                // Strip HTML tags from text content for clean export
                const cleanContent = stripHtmlTags(block.content);
                if (cleanContent.trim()) {
                  blocks.push({
                    type: 'text',
                    content: cleanContent,
                    index: currentIndex++
                  });
                }
              } else if (block.type === 'image' && block.metadata?.src) {
                blocks.push({
                  type: 'image',
                  content: block.metadata.src,
                  index: currentIndex++
                });
              }
            });
          }
        });
        
        if (blocks.length === 0) {
          console.log(`  ✅ Document has no blocks, returning empty array`);
          return 'new DocumentBlock[] { }';
        }
        
        // Format blocks as C# array
        const blockStrings = blocks.map(block => {
          const escapedContent = block.content
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r');
          return `new DocumentBlock { type = "${block.type}", content = "${escapedContent}", index = ${block.index} }`;
        });
        
        console.log(`  ✅ Generated ${blocks.length} document blocks`);
        return `new DocumentBlock[] { ${blockStrings.join(', ')} }`;
      }
    }
    
    if (value === null || value === undefined || value === '') {
      if (mappedType.includes('bool')) return 'false';
      if (mappedType.includes('int') || mappedType.includes('float') || mappedType.includes('double')) return '0';
      if (mappedType.includes('[]') || mappedType.includes('Array')) return 'new string[] { }';
      return '""';
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'new string[0]';
      }
      const arrItems = value.map(v => formatValueCSharp(v, 'string')).join(', ');
      return `new[] { ${arrItems} }`;
    }
    
    if (typeof value === 'string') {
      const escaped = value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      return `"${escaped}"`;
    }
    
    if (typeof value === 'number') {
      if (mappedType.includes('float')) return `${value}f`;
      // If mapped as int but value has decimals, format as double
      if (mappedType.includes('int') && !Number.isInteger(value)) return value.toString();
      return value.toString();
    }
    
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    
    return `"${String(value)}"`;
  };
  
  // Start building C# file
  let output = '// Auto-generated Runtime Content Database\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n';
  output += 'using System.Linq;\n';
  output += 'using Grimoire.Data.Runtime;\n\n';
  output += `namespace ${namespace}\n{\n`;
  output += `\tpublic static class ${className}\n\t{\n`;
  
  // Generate #region Templates with base class and template classes
  output += '\t\t#region Templates\n\n';
  output += '\t\tpublic abstract class Template { }\n\n';
  
  // DocumentBlock class for document field types
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Represents a single block of content from a document\n';
  output += '\t\t/// </summary>\n';
  output += '\t\t[Serializable]\n';
  output += '\t\tpublic class DocumentBlock\n\t\t{\n';
  output += '\t\t\tpublic string type;     // "text" or "image"\n';
  output += '\t\t\tpublic string content;  // Text content or image URL\n';
  output += '\t\t\tpublic int index;       // Order in the document\n';
  output += '\t\t}\n\n';
  
  // Generate a class for each blueprint
  blueprintGroups.forEach((group, blueprintId) => {
    const blueprintName = group.blueprint.name || blueprintId;
    const safeClassName = applyNamingConvention(blueprintName, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
    
    // Get fields from first item
    if (group.items.length === 0) return;
    const firstItem = group.items[0];
    const fields = extractFields(firstItem, template, blueprints, blueprintName, lookupContext, useTranslationKeys);
    
    output += `\t\tpublic sealed class ${safeClassName} : Template\n\t\t{\n`;
    
    fields.forEach(field => {
      const fieldName = applyNamingConvention(field.label, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
      const actualValue = field.value !== undefined ? field.value : field.defaultValue;
      
      const isTranslatable = field.isTranslatable;
      
      console.log(`🔍 Template field "${field.label}": isTranslatable=${isTranslatable}, isReference=${field.isReference}`);
      
      // Handle dialog references with lazy properties (similar to glossaryItem)
      if (field.isReference && field.referenceType === 'dialog') {
        const isArray = field.isMultiple;
        
        console.log(`🎭 Processing dialog field: "${field.label}" (id=${field.id}, isArray=${isArray})`);
        
        if (isArray) {
          output += `\t\t\tpublic string[] ${fieldName}_ids;\n`;
          output += `\t\t\tpublic DialogSection[] ${fieldName} => ${fieldName}_ids?.Select(id => DialogDatabase.Dialogs.TryGetValue(id, out var d) ? d : null).Where(x => x != null).ToArray();\n`;
        } else {
          output += `\t\t\tpublic string ${fieldName}_id;\n`;
          output += `\t\t\tpublic DialogSection ${fieldName} => !string.IsNullOrEmpty(${fieldName}_id) && DialogDatabase.Dialogs.TryGetValue(${fieldName}_id, out var d) ? d : null;\n`;
        }
      }
      // Handle glossaryItem references with lazy properties
      else if (field.isReference && field.referenceType === 'glossaryItem') {
        // Use field.isMultiple as the sole source of truth for array vs single
        const isArray = field.isMultiple;
        
        let referencedTypeName: string | null = null;
        
        console.log(`🔎 Processing glossaryItem field: "${field.label}" (id=${field.id}, isArray=${isArray}, isMultiple=${field.isMultiple})`);
        console.log(`   field.filterOptions:`, field.filterOptions);
        
        // PRIORITY 1: Check field.filterOptions (attached during field extraction)
        if (field.filterOptions && field.filterOptions.length > 0) {
          console.log(`   ✓ Found filterOptions on field: ${JSON.stringify(field.filterOptions)}`);
          if (field.filterOptions.length === 1) {
            const targetBlueprintId = field.filterOptions[0];
            const targetBlueprint = blueprints.find(bp => bp.id === targetBlueprintId);
            console.log(`   Target blueprint for ${targetBlueprintId}: ${targetBlueprint ? targetBlueprint.name : 'NOT FOUND'}`);
            if (targetBlueprint) {
              referencedTypeName = applyNamingConvention(targetBlueprint.name || targetBlueprintId, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
              console.log(`✅ Determined type from field.filterOptions: ${referencedTypeName} for field ${field.label} (isArray=${isArray})`);
            }
          } else {
            // Multiple filterOptions - will check referenceData to see if all items share same type
            console.log(`   ℹ️ Field has multiple filterOptions (${field.filterOptions.length}), checking referenceData...`);
          }
        }
        
        // PRIORITY 2: If filterOptions didn't give us a single type, check referenceData
        if (!referencedTypeName && field.referenceData) {
          // Normalize referenceData to array for consistent processing
          const refDataArray = Array.isArray(field.referenceData) ? field.referenceData : [field.referenceData];
          
          // Check if all items share the same blueprint_id
          const blueprintIds = refDataArray
            .filter(item => item && item.blueprint_id)
            .map(item => item.blueprint_id);
          
          if (blueprintIds.length > 0) {
            const uniqueBlueprintIds = [...new Set(blueprintIds)];
            if (uniqueBlueprintIds.length === 1) {
              // All items share the same blueprint type
              const sharedBlueprintId = uniqueBlueprintIds[0];
              const refBlueprint = blueprints.find(bp => bp.id === sharedBlueprintId);
              if (refBlueprint) {
                referencedTypeName = applyNamingConvention(refBlueprint.name || sharedBlueprintId, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
                console.log(`✅ Determined type from referenceData: ${referencedTypeName} for field ${field.label}`);
              }
            } else {
              console.log(`⚠️ Field ${field.label} references multiple blueprint types:`, uniqueBlueprintIds);
            }
          }
        }
        
        if (isArray) {
          output += `\t\t\tpublic string[] ${fieldName}_ids;\n`;
          const returnType = referencedTypeName || 'Template';
          output += `\t\t\tpublic ${returnType}[] ${fieldName} => ${fieldName}_ids?.Select(id => Library.TryGetValue(id, out var t) ? t as ${returnType} : null).Where(x => x != null).ToArray();\n`;
          // Note: Secondary strongly-typed property removed - primary property now uses strong typing
        } else {
          output += `\t\t\tpublic string ${fieldName}_id;\n`;
          const returnType = referencedTypeName || 'Template';
          output += `\t\t\tpublic ${returnType} ${fieldName} => !string.IsNullOrEmpty(${fieldName}_id) && Library.TryGetValue(${fieldName}_id, out var t) ? t as ${returnType} : null;\n`;
          // Note: Secondary strongly-typed property removed - primary property now uses strong typing
        }
      } else {
        const csharpType = getCSharpType(field.mappedType, actualValue, isTranslatable, field);
        output += `\t\t\tpublic ${csharpType} ${fieldName};\n`;
      }
    });
    
    output += '\t\t}\n\n';
  });
  
  output += '\t\t#endregion\n\n';
  
  // Generate Library dictionary
  output += '\t\tpublic static readonly IReadOnlyDictionary<string, Template> Library = new Dictionary<string, Template>\n\t\t{\n';
  
  // Add each item to the dictionary
  items.forEach(item => {
    const blueprintId = item.blueprint_id;
    if (!blueprintId) return;
    
    const group = blueprintGroups.get(blueprintId);
    if (!group) return;
    
    const blueprintName = group.blueprint.name || blueprintId;
    const safeClassName = applyNamingConvention(blueprintName, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
    
    const folderPath = folderPathResolver(item.folder);
    const itemName = applyNamingConvention(item.name || item.id, namingConventionOverride || template.namingConventions.instanceName);
    const safeItemName = itemName.replace(/[^a-zA-Z0-9_]/g, '_');
    
    let pathKey = '';
    if (folderPath) {
      const cleanPath = folderPath.replace(/^\//, '').replace(/\\/g, '/');
      pathKey = `${cleanPath}/${safeItemName}`;
    } else {
      pathKey = safeItemName;
    }
    
    const fields = extractFields(item, template, blueprints, blueprintName, lookupContext, useTranslationKeys);
    
    output += `\t\t\t["${pathKey}"] = new ${safeClassName}\n\t\t\t{\n`;
    
    fields.forEach(field => {
      const fieldName = applyNamingConvention(field.label, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
      const actualValue = field.value !== undefined ? field.value : field.defaultValue;
      
      console.log(`💎 Instance field "${field.label}": string_ids=${JSON.stringify(field.string_ids)}, value=${JSON.stringify(actualValue).substring(0, 50)}, isReference=${field.isReference}`);
      
      let fieldValue: string;
      let targetFieldName = fieldName;
      
      // Check if this is a dialog reference - store dialog keys instead of resolving
      if (field.isReference && field.referenceType === 'dialog') {
        const isArrayField = field.isMultiple;
        targetFieldName = `${fieldName}_id${isArrayField ? 's' : ''}`;
        
        if (field.referenceData) {
          // Normalize referenceData to array if isMultiple is true
          const refDataArray = isArrayField 
            ? (Array.isArray(field.referenceData) ? field.referenceData : [field.referenceData])
            : null;
          
          if (isArrayField && refDataArray) {
            // Multiple field - always output as array
            const dialogKeys = refDataArray.map(refObj => {
              // Use dialog_key as the reference key
              const dialogKey = refObj.dialog_key || refObj.name || refObj.id;
              return `"${dialogKey}"`;
            }).join(', ');
            fieldValue = `new[] { ${dialogKeys} }`;
            console.log(`✅ Storing dialog array IDs: ${dialogKeys}`);
          } else {
            // Single field - output as single value
            const refData = Array.isArray(field.referenceData) ? field.referenceData[0] : field.referenceData;
            if (refData) {
              const dialogKey = refData.dialog_key || refData.name || refData.id;
              fieldValue = `"${dialogKey}"`;
              console.log(`✅ Storing dialog ID: ${dialogKey}`);
            } else {
              fieldValue = '""';
              console.log(`✅ Empty dialog reference`);
            }
          }
        } else {
          // Empty dialog reference - use empty string or array
          if (isArrayField) {
            fieldValue = 'new string[0]';
            console.log(`✅ Empty dialog array reference`);
          } else {
            fieldValue = '""';
            console.log(`✅ Empty dialog reference`);
          }
        }
      }
      // Check if this is a glossaryItem reference - store IDs instead of resolving
      else if (field.isReference && field.referenceType === 'glossaryItem') {
        // Use field.isMultiple as the primary source of truth for array vs single
        const isArrayField = field.isMultiple;
        targetFieldName = `${fieldName}_id${isArrayField ? 's' : ''}`;
        
        if (field.referenceData) {
          // Normalize referenceData to array if isMultiple is true
          const refDataArray = isArrayField 
            ? (Array.isArray(field.referenceData) ? field.referenceData : [field.referenceData])
            : null;
          
          if (isArrayField && refDataArray) {
            // Multiple field - always output as array
            const pathKeys = refDataArray.map(refObj => {
              const itemKey = applyNamingConvention(refObj.name || refObj.id, namingConventionOverride || template.namingConventions.instanceName);
              const refFolderPath = folderPathResolver(refObj.folder);
              const refPathKey = refFolderPath ? `${refFolderPath.replace(/^\//, '').replace(/\\/g, '/')}/${itemKey}` : itemKey;
              return `"${refPathKey}"`;
            }).join(', ');
            fieldValue = `new[] { ${pathKeys} }`;
            console.log(`✅ Storing glossaryItem array IDs: ${pathKeys}`);
          } else {
            // Single field - output as single value
            const refData = Array.isArray(field.referenceData) ? field.referenceData[0] : field.referenceData;
            if (refData) {
              const itemKey = applyNamingConvention(refData.name || refData.id, namingConventionOverride || template.namingConventions.instanceName);
              const refFolderPath = folderPathResolver(refData.folder);
              const refPathKey = refFolderPath ? `${refFolderPath.replace(/^\//, '').replace(/\\/g, '/')}/${itemKey}` : itemKey;
              fieldValue = `"${refPathKey}"`;
              console.log(`✅ Storing glossaryItem ID: ${refPathKey}`);
            } else {
              fieldValue = '""';
              console.log(`✅ Empty glossaryItem reference`);
            }
          }
        } else {
          // Empty glossaryItem reference - use empty string or array
          if (isArrayField) {
            fieldValue = 'new string[0]';
            console.log(`✅ Empty glossaryItem array reference`);
          } else {
            fieldValue = '""';
            console.log(`✅ Empty glossaryItem reference`);
          }
        }
      }
      // Check if this field is a reference field (types/tags)
      else if (field.isReference && field.referenceData && field.referenceType) {
        fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
      }
      // Check if this field has string_ids (translatable field with array)
      else if (field.string_ids && field.string_ids.length > 0) {
        if (Array.isArray(actualValue) && actualValue.length === field.string_ids.length) {
          const keyRefsArray = field.string_ids.map(stringId => {
            const stringItem = stringsMap.get(stringId);
            if (stringItem) {
              const keyName = stringItem.abbrev || `key_${stringItem.id}`;
              const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
              console.log(`✅ Using TranslationKey: ${safeKeyName} for string_id: ${stringId}`);
              return `TranslationKeys.${safeKeyName}`;
            }
            return null;
          }).filter(Boolean);
          
          fieldValue = keyRefsArray.length > 0 
            ? `new[] { ${keyRefsArray.join(', ')} }`
            : 'new TranslationKey[0]';
        } else if (field.string_ids.length === 1) {
          const stringItem = stringsMap.get(field.string_ids[0]);
          if (stringItem) {
            const keyName = stringItem.abbrev || `key_${stringItem.id}`;
            const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
            console.log(`✅ Using TranslationKey: ${safeKeyName} for field "${field.label}"`);
            fieldValue = `TranslationKeys.${safeKeyName}`;
          } else {
            fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
          }
        } else {
          fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
        }
      } else if (field.isTranslatable) {
        const isEmpty = actualValue === null || actualValue === undefined || actualValue === '' || 
                       (Array.isArray(actualValue) && actualValue.length === 0);
        if (isEmpty) {
          if (Array.isArray(actualValue)) {
            fieldValue = 'new TranslationKey[0]';
          } else {
            fieldValue = 'TranslationKeys.Empty';
          }
        } else {
          fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
        }
      } else {
        fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
      }
      
      output += `\t\t\t\t${targetFieldName} = ${fieldValue},\n`;
    });
    
    output += '\t\t\t},\n';
  });
  
  output += '\t\t};\n\n';
  
  // Add helper method to safely get typed items from Library or DialogDatabase
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Unified Get method that can access both Library items and Dialog sections.\n';
  output += '\t\t/// For dialog sections, use the format: "dialogKey/sectionId"\n';
  output += '\t\t/// For library items, use the folder path format: "folder/subfolder/itemName"\n';
  output += '\t\t/// </summary>\n';
  output += '\t\tpublic static T Get<T>(string key) where T : class\n';
  output += '\t\t{\n';
  
  // Check if dialogs are present to enable dialog lookup
  if (dialogs && dialogs.length > 0) {
    output += '\t\t\t// First try DialogDatabase.Dialogs if T is a DialogSection\n';
    output += '\t\t\tif (typeof(T).IsSubclassOf(typeof(DialogSection)) || typeof(T) == typeof(DialogSection))\n\t\t\t{\n';
    output += '\t\t\t\tif (DialogDatabase.Dialogs.TryGetValue(key, out var dialogSection))\n\t\t\t\t{\n';
    output += '\t\t\t\t\treturn dialogSection as T;\n';
    output += '\t\t\t\t}\n';
    output += '\t\t\t\tGrimoireLog.Warn($"[Database] Dialog section not found: \'{key}\'");\n';
    output += '\t\t\t\treturn null;\n';
    output += '\t\t\t}\n\n';
  }
  
  output += '\t\t\t// Try Library for Template types\n';
  output += '\t\t\tif (typeof(T).IsSubclassOf(typeof(Template)) || typeof(T) == typeof(Template))\n\t\t\t{\n';
  output += '\t\t\t\tif (!Library.TryGetValue(key, out var item))\n\t\t\t\t{\n';
  output += '\t\t\t\t\tGrimoireLog.Warn($"[Database] Key not found: \'{key}\'");\n';
  output += '\t\t\t\t\treturn null;\n';
  output += '\t\t\t\t}\n\n';
  output += '\t\t\t\tif (item is not T typed)\n\t\t\t\t{\n';
  output += '\t\t\t\t\tGrimoireLog.Error(\n';
  output += '\t\t\t\t\t\t$"[Database] Key \'{key}\' is of type {item.GetType().Name}, " +\n';
  output += '\t\t\t\t\t\t$"but requested {typeof(T).Name}"\n';
  output += '\t\t\t\t\t);\n';
  output += '\t\t\t\t\treturn null;\n';
  output += '\t\t\t\t}\n\n';
  output += '\t\t\t\treturn typed;\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\treturn null;\n';
  output += '\t\t}\n';
  
  // Add Dialogs section if dialogs are provided
  if (dialogs && dialogs.length > 0) {
    output += '\n';
    output += '\t\tpublic static class Dialogs\n';
    output += '\t\t{\n';
    
    dialogs.forEach(dialog => {
      const dialogIdentifier = applyNamingConvention(dialog.dialog_key || dialog.name, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
      output += `\t\t\tpublic static class ${dialogIdentifier}\n`;
      output += '\t\t\t{\n';
      output += `\t\t\t\tpublic static readonly string Id = "${dialog.id}";\n`;
      output += `\t\t\t\tpublic static readonly string Name = "${(dialog.name || '').replace(/"/g, '\\"')}";\n`;
      output += `\t\t\t\tpublic static readonly string DialogKey = "${dialog.dialog_key || dialog.name}";\n`;
      
      // Render sections
      if (dialog.data && dialog.data.sections && Array.isArray(dialog.data.sections)) {
        dialog.data.sections.forEach((section: any) => {
          const sectionId = applyNamingConvention(section.section_identifier || section.name || 'section', fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
          output += `\n\t\t\t\tpublic static class ${sectionId}\n`;
          output += '\t\t\t\t{\n';
          output += `\t\t\t\t\tpublic static readonly string SectionId = "${section.section_identifier || section.name || ''}";\n`;
          
          // Render fields
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any, fieldIdx: number) => {
              const dialogFieldName = applyNamingConvention(`field_${fieldIdx}_${field.type}`, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
              
              // Field text
              if (field.text) {
                if (field.string_id) {
                  const stringItem = stringsMap.get(field.string_id);
                  if (stringItem) {
                    const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                    const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                    output += `\t\t\t\t\tpublic static readonly TranslationKey ${dialogFieldName}_text = TranslationKeys.${safeKeyName};\n`;
                  } else {
                    const escapedText = field.text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                    output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_text = "${escapedText}";\n`;
                  }
                } else {
                  const escapedText = field.text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                  output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_text = "${escapedText}";\n`;
                }
              }
              
              output += `\t\t\t\t\tpublic static readonly string ${dialogFieldName}_type = "${field.type}";\n`;
              
              // Options for questions
              if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                output += `\t\t\t\t\tpublic static class ${dialogFieldName}_options\n`;
                output += '\t\t\t\t\t{\n';
                field.options.forEach((option: any, optIdx: number) => {
                  const optionName = applyNamingConvention(`option_${optIdx}`, fieldConvention).replace(/[^a-zA-Z0-9_]/g, '_');
                  if (option.string_id) {
                    const stringItem = stringsMap.get(option.string_id);
                    if (stringItem) {
                      const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                      const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                      output += `\t\t\t\t\t\tpublic static readonly TranslationKey ${optionName} = TranslationKeys.${safeKeyName};\n`;
                    } else {
                      const escapedText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                      output += `\t\t\t\t\t\tpublic static readonly string ${optionName} = "${escapedText}";\n`;
                    }
                  } else {
                    const escapedText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                    output += `\t\t\t\t\t\tpublic static readonly string ${optionName} = "${escapedText}";\n`;
                  }
                });
                output += '\t\t\t\t\t}\n';
              }
            });
          }
          
          output += '\t\t\t\t}\n';
        });
      }
      
      output += '\t\t\t}\n\n';
    });
    
    output += '\t\t}\n';
    console.log(`✅ Rendered ${dialogs.length} dialogs in Runtime Database`);
  }
  
  output += '\t}\n';
  output += '}\n';
  
  console.log('✅ Runtime Content Database C# template rendered successfully');
  
  return output;
}
